/**
 * WhatsNewPage — public customer-facing changelog.
 *
 * Reads /api/changelog (no auth required) and renders only iterations
 * that opted in via a `Customer summary:` line in PRD.md. Internal
 * markdown / file paths / test counts are filtered out server-side.
 *
 * Designed to look like a marketing changelog page (vs. the dense
 * audit-style ImprovementLogPage).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowLeft, Calendar, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT, useI18n } from "@/lib/i18n";
import LanguagePicker from "@/components/LanguagePicker";

const CAT_TONE = {
  feature: "bg-emerald-100 text-emerald-800",
  fix: "bg-rose-100 text-rose-800",
  refactor: "bg-amber-100 text-amber-800",
  docs: "bg-blue-100 text-blue-800",
};

export default function WhatsNewPage() {
  const t = useT();
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    api.get("/changelog?limit=100")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error(t("whats_new.subscribe_email_invalid", "Please enter a valid email."));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/changelog/subscribe", { email: email.trim(), lang });
      setSubscribed(true);
      if (r.data?.already_confirmed) {
        toast.success(t("whats_new.subscribe_already_confirmed", "Welcome back — you're already on the list."));
      } else {
        toast.success(t("whats_new.subscribe_check_email", "Check your inbox — we just sent a confirmation link."));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("whats_new.subscribe_failed", "Could not subscribe — please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40" data-testid="whats-new-page">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3">
          <Link to="/" data-testid="whats-new-back"
                className="text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft size={11} weight="bold" />
            {t("whats_new.back", "Back to portal")}
          </Link>
          <span className="ml-auto"><LanguagePicker /></span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <section className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-md mb-4">
            <Sparkle size={22} weight="fill" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight font-['Chivo'] text-slate-900">
            {t("whats_new.title", "What's New")}
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            {t("whats_new.subtitle", "Recent improvements to your portal — written for you, not for engineers.")}
          </p>
        </section>

        {loading ? (
          <p className="text-center text-sm text-slate-500 py-12">{t("whats_new.loading", "Loading…")}</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-slate-500 italic py-12" data-testid="whats-new-empty">
            {t("whats_new.empty", "No customer-facing updates posted yet — check back soon.")}
          </p>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-4 space-y-6" data-testid="whats-new-list">
            {items.map((it) => (
              <li key={it.iter_id} className="ml-6" data-testid={`whats-new-${it.iter_id}`}>
                <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-blue-600 border-2 border-white shadow" />
                <article className="bg-white border border-slate-200 rounded-md p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <Calendar size={10} weight="bold" />
                      {it.date}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${CAT_TONE[it.category] || "bg-slate-100 text-slate-700"}`}>
                      {it.category}
                    </span>
                  </div>
                  <h2 className="text-base font-bold text-slate-900 leading-tight mb-2">{it.title}</h2>
                  <p className="text-sm text-slate-700 leading-relaxed">{it.customer_summary}</p>
                </article>
              </li>
            ))}
          </ol>
        )}

        <footer className="text-center pt-8 pb-4 text-[11px] text-slate-400">
          {t("whats_new.footer", "Have a feature request? Reach us through the Conversations page.")}
        </footer>

        {/* Subscribe widget */}
        <section className="mt-6 bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid="whats-new-subscribe">
          {subscribed ? (
            <div className="text-center text-sm" data-testid="whats-new-subscribe-done">
              <CheckCircle size={32} weight="fill" className="mx-auto text-emerald-600 mb-2" />
              <p className="font-bold text-slate-900">{t("whats_new.subscribe_check_inbox_title", "Check your inbox to confirm.")}</p>
              <p className="text-xs text-slate-500 mt-1">{t("whats_new.subscribe_check_inbox_hint", "We just sent a confirmation link. Click it to start receiving short digests in your language. No spam — unsubscribe in one click.")}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <EnvelopeSimple size={16} weight="bold" className="text-blue-700" />
                <h3 className="text-sm font-bold tracking-tight">{t("whats_new.subscribe_title", "Get notified when we ship something new")}</h3>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {t("whats_new.subscribe_subtitle", "One short email, in your language, only when there's something visible to you. Unsubscribe anytime.")}
              </p>
              <form onSubmit={onSubscribe} className="flex flex-wrap items-center gap-2" data-testid="whats-new-subscribe-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("whats_new.subscribe_placeholder", "you@company.com")}
                  data-testid="whats-new-subscribe-email"
                  className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="whats-new-subscribe-btn"
                  className="px-4 py-2 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-40">
                  {submitting ? t("whats_new.subscribing", "Subscribing…") : t("whats_new.subscribe_btn", "Subscribe")}
                </button>
              </form>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
